import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import type { TransmittalReference } from "@Features/transmittals/hooks/transmittal.types";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import { formatBytes, containerFileTypes, TOAST_TYPE_ERROR } from "@Services/constants";
import React, { useEffect, useImperativeHandle, useState } from "react";
import { useDispatch } from "react-redux";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import IconComponent from "@CubeComponents/icons";
import FileManagerComponent from "@CubeComponents/FileManager/FileManagerComponent";
import type { SelectedFileItem } from "@ServerTypes/folders/folders.schema";
import type { AppDispatch } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { ColumnDefinition, ToolbarItem } from "@CubeComponents/html/SlickGrid1";
import type { TranslatedString } from "@i18n";

/**
 * A toolbar entry as this component builds it. `ToolbarItem` declares the label as `text`, but every
 * item here sets `name` instead — so the shared type is extended rather than the values reshaped.
 * Worth reconciling: one of the two spellings is being ignored at render time.
 */
type FileManagerToolbarItem = ToolbarItem & {
  name?: string;
  /** Visibility gate applied by this component before handing the list to the grid. */
  show?: unknown;
  ["data-testid"]?: string;
};

/** The SlickGrid dataView surface this component drives directly. */
type GridDataView = {
  getItemById?: (id: string) => FileRow | undefined;
  getItems?: () => FileRow[];
  items?: FileRow[];
  updateItem?: (id: string, item: FileRow) => void;
};

/**
 * A row of the file-manager grid, as this component tags and renders it. `isSpaceRoot` is a
 * client-only marker (a Space's top level is not a folder document). No index signature —
 * `SelectedFileItem` is Zod-derived, and adding one would stop plain `SelectedFileItem[]` from
 * satisfying callbacks typed against this.
 */
type FileRow = SelectedFileItem & {
  isSpaceRoot?: boolean;
  /** Written locally by the rename flow in `useFileManager`; not part of `folderItemSchema`. */
  dateModified?: number;
  modifiedBy?: string;
  revision?: string;
  issueVersionId?: ObjectIdString;
  revisionSeries?: { label?: string } | null;
  statusInReviews?: { name?: string };
  isRowDisabled?: boolean;
};

/**
 * One derived custom-field grid column, as `getSubColumns` (SlickGrid1/customFields.tsx) builds it.
 * The keys that column really sets are declared, so they can be read without reaching through the
 * index signature; the signature stays for the rest of the SlickGrid column surface.
 */
type TemplateColumn = {
  _id?: string;
  id?: string;
  field?: string;
  headerText?: string;
  name?: string;
  /** Grouped-header keys; dropped when the column is promoted to the identity column. */
  columnGroup?: string;
  columnGroupId?: string;
  _valueGetter?: (row: FileRow) => unknown;
  formatter?: (d: number, p: number, a: unknown, b: unknown, row: FileRow) => unknown;
  [key: string]: unknown;
};

/** An entry of the "New"/import dropdown, which may carry a nested submenu. */
type DropdownEntry = {
  id: string;
  text?: string;
  iconName?: string;
  iconCss?: string;
  subItems?: DropdownEntry[];
  [key: string]: unknown;
};
import _ from "lodash";
import { Box, Divider, Fade, FormControl, Menu, MenuItem, Select } from "@mui/material";
import CreateReviewModal from "@Features/reviews/CreateReviewModal";
import { ExternalLinksHost, MoveBreaksLinksModal, getExternalLinkCounts } from "../ExternalLinks";
import * as Sentry from "@Sentry";
import { FileConversionStatus } from "@Features/files";
import FileDelete from "./FileDelete/FileDelete";
import ExcelImport from "@Components/ExcelImport/ExcelImport";
import { excelImportSources } from "@Components/ExcelImport/ExcelHelper";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import Text from "@CubeComponents/html/Text/Text";
import CreateTransmittal from "@Features/transmittals/pages/Transmitals/CreateTransmittal/CreateTransmittal";
import { convertDate } from "@Services/luxonHelper";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { getFileIcon, getIconbasedOnResourceType } from "@CubeComponents/constants/globalFunctions";
import { NomenclatureField } from "@Services/utils/useCustomFields";
import FormPreview from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/FormTemplate/formPreview";
import FileManagerAsPopup from "./FileManagerAsPopup";
import "./FileManagerNew.css";
import FolderSyncTemplate from "./FolderSyncTemplate";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import useFileManager from "./useFileManager";
import { showToastNotification } from "@Reducers/toastSlice";
import { hasInvalidChars, isDocumentNumberField } from "@Helpers/constants";
import { useLocation, useNavigate } from "react-router-dom";
import { getSubColumns } from "@CubeComponents/html/SlickGrid1/customFields";
import { getStatusBtnStyle } from "@Features/reviews/helpers";
import Forbidden from "@Pages/Error/Forbidden";
import useUserModuleAccess from "@Services/userModuleAccessBySheetHook";
import ProjectModuleNoAccessIllustration from "@Components/ProjectModulesAccessIllustration";
import { PORTAL_NO_SEAT } from "helpers";
import { ArrowDropDownIcon } from "@mui/x-date-pickers/icons";
import { FormTemplateField } from "@ServerTypes/forms/forms.schema";

interface FileManagerProps {
  defaultSelectedTab?: string;
  fileManagerAsPopup?: boolean;
  spaceId: string;
  projectId: string;
  // Project-level ("Folders") surface: render every Space of the project as a tree root.
  projectMode?: boolean;
  showOnlyMergedFile?: boolean;
  setShowProperties?: (update: boolean | ((prev: boolean) => boolean)) => void;
  mobileFileManager?: boolean;
  hideExternalSpace?: boolean;
  hideReviews?: boolean;
  hideAllTabs?: boolean;
  enableSpaceListDropdown?: boolean;
  openThisSpaceByDefault?: boolean;
  createShortcut?: boolean;
  showFolderTree?: boolean;
  handleFileSelectionCallback?: (files: SelectedFileItem[]) => void;
  hideIssues?: boolean;
  hideTasks?: boolean;
  hideEstimates?: boolean;
  hideClashTests?: boolean;
  hideContracts?: boolean;
  hideSchedules?: boolean;
  useAttachedExternalVolume?: boolean;
  hideMergeFiles?: boolean;
  disableContextMenu?: boolean;
  disableFiles?: (string | undefined)[];
  enableOnlyShareableResources?: boolean;
  hideUpload?: boolean;
  enableFilesTypes?: string[] | null;
  hideShortcuts?: boolean;
  handleFolderSelectionCallback?: (folder: SelectedFileItem) => void;
  setSpaceId?: (id: ObjectIdString) => void;
  alreadySelected?: SelectedFileItem[] | null;
  showExcelImport?: boolean;
  defaultPath?: string;
  allowFolderSelectionCallback?: boolean;
  spaceList?: { _id?: ObjectIdString; spaceName?: string }[];
  disableDragAndDropUpload?: boolean;
  hideFilesWithoutVersion?: boolean;
  /**
   * DEAD PROPS: passed by the mobile file explorer (`pages/FileExplorer/Mobile.tsx`) but read by
   * neither this component nor `useFileManager`. Declared so that call site type-checks without an
   * `any` spread; dropping them from the caller would be a behaviour-neutral cleanup.
   */
  fileDetails?: () => void;
  workspaceId?: string;
}

/**
 * `slickGrid.getData()` is declared `unknown` — SlickGrid hands back either its DataView or the raw
 * item array — so the surface is checked at runtime instead of asserted. Every member of
 * `GridDataView` is optional, so each is verified only when the value actually carries it; a value
 * carrying one at the wrong type is rejected here rather than reaching the reads below (in
 * particular `items`, which is spread into `.find()` without a further probe).
 */
const isGridDataView = (value: unknown): value is GridDataView => {
  if (typeof value !== "object" || value === null) return false;
  if ("getItemById" in value && typeof value.getItemById !== "function") return false;
  if ("getItems" in value && typeof value.getItems !== "function") return false;
  if ("updateItem" in value && typeof value.updateItem !== "function") return false;
  if ("items" in value && value.items !== undefined && !Array.isArray(value.items)) return false;
  return true;
};

export interface FileManagerHandle {
  updateRowCustomFields: (rowId: string, changes: Record<string, unknown>) => void;
  /** Navigate the tree into a folder by id — used by the FileExplorer app-header breadcrumb. */
  navigateToFolder: (folderId: string) => void;
}

const FileManager = React.forwardRef<FileManagerHandle, FileManagerProps>((props, ref) => {
  const dispatch = useDispatch<AppDispatch>();
  const { disableFiles, hideFilesWithoutVersion } = props;
  const navigate = useNavigate();
  const location = useLocation();

  const {
    fileManagerRef,
    formValidator,
    pollingRef,

    fileManagerData,
    setFileManagerData,
    showContainerImportModal,
    setShowContainerImportModal,
    importMode,
    openRenameModal,
    setOpenRenameModal,
    selectedFile,
    newName,
    setNewName,
    folderName,
    createReviewModal,
    openMergeModal,
    openMergeEstimateModal,
    openFolderCreationModal,
    openFileImport,
    openContainerModal,
    currentFolder,
    setCurrentFolder,
    selectedFilesToDelete,
    openDeleteConfirmationModal,
    openCreateTransmittalModal,
    setOpenCreateTransmittalModal,
    containerFormTemplate,
    fileTypeValue,
    resourceType,
    setResourceType,
    containerName,
    path,
    preSearchText,
    toolBarBtnState,
    anchorEl,
    open,
    importSubmenuAnchor,
    importSubmenuOpen,

    isFileVersionUploading,
    spaceData,
    handleDeleteModalClose,
    handleDeleteSuccess,
    getSelectedFilesInFileManager,
    getPermission,
    beforeSending,
    handleSelectOption,
    handleClick,
    handleClose,
    handleImportSubmenuOpen,
    handleImportSubmenuClose,
    closeReviewModal,
    onFileSelect,
    onFileClick,
    toolbarClick,
    onSuccess,
    saveFileMergeModal,
    closeFileMergeModal,
    closeFileImportPopup,
    closeMergeEstimateModal,
    handleFileImportCallback,
    createMergeEstimateModal,
    onFileLoad,
    handleModalClose,
    handleCreateContainer,
    handleSaveNewName,
    handleCloseFolder,
    handleFolderName,
    handleCustomFieldsData,
    handleEmbedUrl,
    handleContainerName,
    handleCreateFolder,
    handleNomenclatureChange,
    nomenclatureValues,
    menuOpen,
    setFileTypeValue,

    fileManagerOptionsMapper,
    dropDownButtonItems,
    externalLinks,
    pendingMoveWarning,
    setPendingMoveWarning,
    confirmPendingMove,

    fileManagerAsPopup,
    disableContextMenu,
    showFolderTree,
    disableDragAndDropUpload,
    spaceId,
    projectId,
    projectMode,
    reloadFileManager,
    layout,
    t,
  } = useFileManager({ ...props, navigate, location });

  const pinDocumentAndRevisionColumns = !!layout.FILE_MANAGER?.PIN_DOCUMENT_AND_REVISION_COLUMNS;

  // Seat/licence gate for the project-level "Folders" surface (same pattern as Contracts/Forms pages).
  const moduleAccess = useUserModuleAccess();

  useImperativeHandle(
    ref,
    () => ({
      // Navigate the file manager into a folder by id — used by the FileExplorer app-header breadcrumb.
      navigateToFolder: (folderId: string) => fileManagerRef.current?.navigateToFolder?.(folderId),
      updateRowCustomFields: (rowId: string, changes: Record<string, unknown>) => {
        if (!rowId) return;
        const grid = fileManagerRef.current;
        const slickGrid = (
          grid as
            | (typeof grid & { slickGrid?: { getData?: () => unknown; invalidate?: () => void; render?: () => void } })
            | null
        )?.slickGrid;

        const dataView: unknown = grid?.dataView ?? slickGrid?.getData?.();
        if (!slickGrid || !isGridDataView(dataView)) return;
        const id = String(rowId);
        let existing: FileRow | undefined;
        if (typeof dataView.getItemById === "function") {
          existing = dataView.getItemById(id);
        } else {
          // Widened on assignment: `GridDataView` declares these rows non-nullable, but the object
          // they come off was probed at runtime, not validated element by element — the array is
          // read at the type it can actually hold so the lookup below stays null-safe.
          const gridItems: Array<FileRow | undefined> = dataView.getItems?.() ?? dataView.items ?? [];
          existing = gridItems.find((x) => String(x?.id) === id);
        }
        if (!existing) return;
        const updated = _.cloneDeep(existing);
        for (const key of Object.keys(changes)) {
          _.set(updated, key, changes[key]);
        }
        if (typeof dataView.updateItem === "function") {
          dataView.updateItem(id, updated);
        }
        slickGrid.invalidate!();
        slickGrid.render!();
      },
    }),
    [fileManagerRef],
  );

  const fileManagerDataWithDisabled = React.useMemo(() => {
    const hasDisable = Array.isArray(disableFiles) && disableFiles.length > 0;
    if (!hasDisable && !hideFilesWithoutVersion) return fileManagerData;
    const disabledSet = new Set(hasDisable ? disableFiles : []);
    const tag = (file: FileRow) => {
      const shouldDisable =
        hasDisable &&
        (((["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE"] as (string | undefined)[]).includes(file.resourceType) &&
          disabledSet.has(file.isEmptyContainer ? file.id : file.fileVersion)) ||
          (file.resourceType === "RESOURCE_TYPE_ISSUE" && disabledSet.has(file.issueVersionId)) ||
          (file.resourceType === "RESOURCE_TYPE_TASKS" && disabledSet.has(file.taskId)));
      return shouldDisable ? { ...file, isRowDisabled: true } : file;
    };
    const keep = (file: FileRow) => {
      if (!hideFilesWithoutVersion) return true;
      if (file.resourceType === "RESOURCE_TYPE_FILE" || file.resourceType === "RESOURCE_TYPE_EMBED") {
        return !file.isEmptyContainer && !!file.fileVersion;
      }
      return true;
    };
    const transform = (arr: FileRow[]) => arr.filter(keep).map(tag);
    return {
      ...fileManagerData,
      files: Array.isArray(fileManagerData.files) ? transform(fileManagerData.files) : fileManagerData.files,
      filteredFiles: Array.isArray(fileManagerData.filteredFiles)
        ? transform(fileManagerData.filteredFiles)
        : fileManagerData.filteredFiles,
    };
  }, [fileManagerData, disableFiles, hideFilesWithoutVersion]);

  const [customFieldColumns, setCustomFieldColumns] = useState<TemplateColumn[]>([]);
  /** Form-template `_id`s that have already contributed their columns to `customFieldColumns`. */
  const builtTemplateIdsRef = React.useRef<Set<string>>(new Set());

  useEffect(() => {
    const byFolder = fileManagerData.customFieldsByFolder;
    const currentId = currentFolder.id ?? "0";
    /**
     * One grouped form template exactly as the server returns it: `getUsedFormTemplatesInDirectory`
     * (packages/server/fileSystemServer/fileSystemServer.service.ts) is a `$group` on `files` keyed by
     * `$formTemplateId`, declared there as `Array<{ _id: ObjectId | null; template: unknown[] }>`.
     * `_id` really is NULL for the rows that carry an inline `formTemplate` with no template id —
     * that null, not a missing element, is what the `!item._id` guard below stands for.
     */
    type UsedFormTemplate = { template?: FormTemplateField[]; _id: ObjectIdString | null };
    const templates: UsedFormTemplate[] = Array.isArray(byFolder?.[currentId])
      ? byFolder[currentId]
      : Array.isArray(fileManagerData.customFields)
        ? fileManagerData.customFields
        : [];
    // Columns ACCUMULATE, they are not rebuilt. A template is identified by its `_id` (the grouped
    // `formTemplateId`, which may be null for files carrying an inline template): a template that
    // already contributed keeps the exact column objects it produced, and only templates met for the
    // first time append new ones. Two reasons the union is the right set rather than "this folder's
    // templates only": the read cache holds files from every folder walked so far and a search shows
    // all of them at once (useFileManagerShared drops the parentId filter while `query` is set), and
    // re-deriving unchanged columns handed the grid new objects on every refetch — which re-seeds
    // `columnDefinitions` downstream and resets the column state the user had set up. Accumulated
    // columns are `initiallyHidden`, so a foreign folder's fields stay in the column chooser.
    const newTemplates = templates.filter((item) => !item._id || !builtTemplateIdsRef.current.has(item._id));
    if (!newTemplates.length) return;

    const addedIds = new Set<string>();
    const added: TemplateColumn[] = [];
    newTemplates.forEach((item: UsedFormTemplate) => {
      if (item._id) builtTemplateIdsRef.current.add(item._id);
      (item.template ?? []).forEach((section: FormTemplateField) => {
        getSubColumns(section as Parameters<typeof getSubColumns>[0]).forEach((col: TemplateColumn) => {
          if (col.id && !addedIds.has(col.id)) {
            addedIds.add(col.id);
            added.push(col);
          }
        });
      });
    });
    if (!added.length) return;

    setCustomFieldColumns((prev) => {
      const existing = new Set(prev.map((col) => col.id));
      const fresh = added.filter((col) => !existing.has(col.id));
      // Same array reference when every candidate is already present, so React skips the re-render.
      return fresh.length ? [...prev, ...fresh] : prev;
    });
  }, [fileManagerData.customFieldsByFolder, fileManagerData.customFields, currentFolder.id]);

  const documentColumn = React.useMemo(
    () => customFieldColumns.find((col) => isDocumentNumberField(col.name)),
    [customFieldColumns],
  );
  const documentColumnId = documentColumn?.id;

  const readDocumentNumber = documentColumn?._valueGetter;
  const gridRows = fileManagerDataWithDisabled.files;

  const hasDocumentNumber = React.useCallback(
    (row: FileRow): boolean => {
      const value = readDocumentNumber?.(row);
      return value !== undefined && value !== null && value !== "" && value !== "-";
    },
    [readDocumentNumber],
  );

  const hasAnyDocumentNumber = React.useMemo(() => {
    if (!readDocumentNumber || !Array.isArray(gridRows)) return false;
    return gridRows.some(hasDocumentNumber);
  }, [readDocumentNumber, hasDocumentNumber, gridRows]);

  const showDocumentNumberAsIdentity = pinDocumentAndRevisionColumns && !!documentColumnId && hasAnyDocumentNumber;

  const showRevisionColumn = React.useMemo(() => {
    if (!pinDocumentAndRevisionColumns || !Array.isArray(gridRows)) return false;
    return gridRows.some((row: FileRow) => !!row.revisionSeries?.label || !!row.revision);
  }, [pinDocumentAndRevisionColumns, gridRows]);

  const pinnedColumnIds = React.useMemo(() => {
    if (!showDocumentNumberAsIdentity) return [];
    return showRevisionColumn ? [String(documentColumnId), "revision"] : [String(documentColumnId)];
  }, [showDocumentNumberAsIdentity, documentColumnId, showRevisionColumn]);

  const statusTemplate = (status: { _id?: string | null; name?: string; category?: string } | undefined) => {
    if (!status || status._id === null) return "-";
    const container = document.createElement("div");
    createRoot(container).render(
      <p className="w-fit rounded px-2 py-1 font-semibold text-white" style={getStatusBtnStyle(status.category)}>
        {status.name}
      </p>,
    );
    return container;
  };

  /**
   * Active external-link counts for the rows currently on screen, keyed by resource id.
   *
   * State alone is NOT enough for the name cell: SlickGrid keeps the formatter closures it was
   * mounted with until the columns re-seed, and the counts request always resolves after the grid
   * mounts. `useFileManagerFiles` therefore includes a signature of these counts in its column
   * re-seed key — that is what carries fresh closures (and so the badge) into the grid. The icons
   * view reads the prop directly in React and needs none of that.
   */
  const [externalLinkCounts, setExternalLinkCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    // Same precedence as the grid itself (`filteredFiles || files`): during a search the visible
    // rows are the filtered set, and those are the rows that need badges.
    const rows = fileManagerData.filteredFiles || fileManagerData.files || [];
    // Only real files/folders can carry a link — estimates, schedules, contracts and transmittal
    // rows are out of scope for v1, so they are not sent to the counts endpoint. Space-root rows
    // are minted with `id = spaceId` and can never match a link, so they are skipped rather than
    // burning the 500-id cap — which project mode (the whole walked tree) can genuinely hit.
    const ids = Array.from(
      new Set(
        rows
          .filter(
            (row: FileRow) =>
              !row.isSpaceRoot &&
              ["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_FOLDER"].includes(
                row.resourceType ?? "",
              ),
          )
          .map((row: FileRow) => String(row.id ?? ""))
          .filter(Boolean),
      ),
    );
    if (!ids.length) {
      setExternalLinkCounts({});
      return;
    }
    let cancelled = false;
    // The endpoint is capped at 500 ids; a longer page is truncated rather than rejected so the
    // badge degrades to "missing on the tail" instead of failing the whole grid.
    getExternalLinkCounts(ids.slice(0, 500))
      .then((res) => {
        if (cancelled || !res.status) return;
        setExternalLinkCounts(res.counts ?? {});
      })
      .catch((error: unknown) => {
        // Reported but never surfaced: the badge is decoration over a grid that has already loaded, so
        // a failed count must not break the listing or raise a toast.
        Sentry.captureException(error);
      });
    return () => {
      cancelled = true;
    };
    // `externalLinks.linksChangedToken` bumps on every create/edit/revoke so the badge follows.
  }, [fileManagerData.files, fileManagerData.filteredFiles, externalLinks.linksChangedToken]);

  const nameTemplate = (args: FileRow) => {
    const container = document.createElement("div");
    container.classList.add("w-full");
    const root = createRoot(container);
    // Only ever a translated warning or the empty default, so the tooltip below takes it directly.
    let content: TranslatedString | "" = "";
    if (!args.isFormTemplateValid) {
      content = t("Mandatory custom fields not filled.");
    } else if (!args.isFileNomenclatureCorrect) {
      content = t("File name does not comply with File nomenclature set for this Folder. Please rename the file.");
    }
    root.render(
      <Box className="flex items-center flex-row gap-1 justify-between name-td">
        <div className="flex gap-2 items-center">
          {/* @ts-expect-error -- same as `FilemanagerFiles.tsx:305`: `ResourceIconInput.resourceType` is required but the row has it optional. */}
          {getIconbasedOnResourceType(args)}
          <Text hideToolTip>{args.name}</Text>
        </div>
        <Box className="flex gap-2">
          <>
            {(!args.isFileNomenclatureCorrect || !args.isFormTemplateValid) &&
              args.resourceType !== "RESOURCE_TYPE_TRANSMITTAL" &&
              !args.transmittalVersionId &&
              content && (
                <IconComponent
                  name={"ErrorIcon"}
                  position="bottom"
                  toolTipContent={content}
                  width="1rem"
                  height="1rem"
                />
              )}
            {/* Shareable external links (CP-283), FR-7 + QA r2 #11: a count pill on the row whenever
                the item carries at least one ACTIVE link (prototype: link glyph + count in a blue
                pill). The counts reach this detached cell through the formatter closure — which
                SlickGrid only refreshes when the columns re-seed; see the note on the state above. */}
            {!!externalLinkCounts[String(args.id)] && (
              <span className="flex items-center gap-1 rounded-full bg-alice_blue px-1.5 text-[0.625rem] font-semibold text-system_colour_21">
                <IconComponent
                  name="Link"
                  position="bottom"
                  toolTipContent={
                    t("Shared externally via {{count}} link(s)", {
                      count: externalLinkCounts[String(args.id)],
                    }) as TranslatedString
                  }
                  width="0.7rem"
                  height="0.7rem"
                />
                {externalLinkCounts[String(args.id)]}
              </span>
            )}
            <FileConversionStatus ref={pollingRef} {...args} />
          </>
        </Box>
      </Box>,
    );
    return container;
  };

  const fileColumns: (ColumnDefinition<FileRow> & { headerText?: string })[] = [
    {
      id: "name",
      field: "name",
      name: t("Name"),
      minWidth: 250,
      filterType: "Text",
      formatter: (data: number, params: number, a: unknown, b: unknown, value: FileRow) => nameTemplate(value),
      initiallyHidden: showDocumentNumberAsIdentity,
    },
    {
      id: "dateModified",
      field: "dateModified",
      name: t("Last Modified on"),
      filterType: "Date",
      formatter: (data: number, params: number, a: unknown, b: unknown, value: FileRow) =>
        convertDate(_.get(value, "dateModified"), "dateForTooltip"),
      minWidth: 150,
    },
    {
      id: "modifiedBy",
      field: "modifiedBy",
      filterType: "Text",
      name: t("Last Modified by"),
      minWidth: 150,
    },
    {
      id: "size",
      field: "size",
      name: t("Size"),
      minWidth: 80,
      filterType: "Number",
      _valueGetter: (value) => (value.size ? formatBytes(value.size) : "-"),
      formatter: (data: number, params: number, a: unknown, b: unknown, value: FileRow) =>
        value.size ? formatBytes(value.size) : "-",
    },
    {
      id: "versionNumber",
      field: "versionNumber",
      name: t("Version"),
      filterType: "Number",
      formatter: (data: number, params: number, a: unknown, b: unknown, value: FileRow) =>
        value.versionNumber ? `V${value.versionNumber}` : "",
      minWidth: 80,
    },
    {
      id: "revision",
      field: "revision",
      name: t("Revision"),
      filterType: "Number",
      initiallyHidden: !showRevisionColumn,
      formatter: (_data: number, _params: number, _a: unknown, _b: unknown, value: FileRow) =>
        value.revisionSeries?.label || "-",
      minWidth: 100,
    },
    {
      id: "statusInReviews",
      field: "statusInReviews",
      filterType: "Text",
      name: t("Status"),
      minWidth: 170,
      initiallyHidden: true,
      _valueGetter: (row: FileRow) => row.statusInReviews?.name || "",
      formatter: (_data: number, _params: number, _a: unknown, _b: unknown, value: FileRow) =>
        statusTemplate(value.statusInReviews),
    },
  ];

  const buildGridColumns = () => {
    const allColumns = [...fileColumns, ...customFieldColumns];
    if (!pinDocumentAndRevisionColumns) return allColumns;
    const ordered = [...allColumns];

    // The document-number column is always the custom field already resolved into `documentColumn`
    // (no built-in column carries a document-number name), so it is located by identity rather than
    // by re-matching the name across both column shapes. `showDocumentNumberAsIdentity` already
    // requires `documentColumnId`, which is what narrows `documentColumn` here.
    if (showDocumentNumberAsIdentity) {
      const documentIndex = ordered.indexOf(documentColumn);
      if (documentIndex > 0) {
        ordered.splice(documentIndex, 1);
        const { columnGroup: _columnGroup, columnGroupId: _columnGroupId, ...ungrouped } = documentColumn;
        const documentValueGetter = documentColumn._valueGetter;
        const documentFormatter = documentColumn.formatter;
        ordered.unshift({
          ...ungrouped,
          initiallyHidden: false,
          _valueGetter: (row: FileRow) => (hasDocumentNumber(row) ? documentValueGetter?.(row) : row.name),
          formatter: (d: number, p: number, a: unknown, b: unknown, row: FileRow) =>
            hasDocumentNumber(row) ? documentFormatter?.(d, p, a, b, row) : nameTemplate(row),
        });
      }
    }

    const revisionIndex = ordered.findIndex((col) => col.id === "revision");
    if (revisionIndex > 1) {
      const revisionColumn = ordered.splice(revisionIndex, 1)[0];
      if (revisionColumn) ordered.splice(1, 0, revisionColumn);
    }
    return ordered;
  };
  const gridColumns = buildGridColumns();

  const getUploadDropDown = () => {
    const permissionCheck = getPermission();
    return (
      <Box className="border ms-5 border-button_light rounded">
        <ButtonComponent
          onClick={handleClick}
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
          endIcon={<ArrowDropDownIcon />}
        >
          <span className="flex items-center gap-2">
            <IconComponent name="add" width="1rem" height="1rem" />
            <Text data-testid="addNewFileBtn">{t("New")}</Text>
          </span>
        </ButtonComponent>
        <Menu
          id="fade-menu"
          slotProps={{
            list: {
              "aria-labelledby": "fade-button",
            },
          }}
          slots={{ transition: Fade }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          {dropDownButtonItems.map((item: DropdownEntry) =>
            item.seperator ? (
              <Divider key={item.id} sx={{ margin: "0px !important" }} />
            ) : item.hasSubmenu ? (
              <MenuItem
                sx={{ fontSize: "0.875rem", paddingBottom: "0.5rem !important", paddingTop: "0.5rem !important" }}
                key={item.id}
                onMouseEnter={handleImportSubmenuOpen}
                onClick={handleImportSubmenuOpen}
              >
                <span className="flex items-center gap-2 w-full justify-between">
                  <span className="flex items-center gap-2">
                    <IconComponent name={item.iconName} width="1rem" height="1rem" />
                    {item.text}
                  </span>
                  <ChevronRightIcon fontSize="small" />
                </span>
              </MenuItem>
            ) : (
              <MenuItem
                sx={{ fontSize: "0.875rem", paddingBottom: "0.5rem !important", paddingTop: "0.5rem !important" }}
                key={item.id}
                onClick={(args) => {
                  if (isFileVersionUploading) {
                    dispatch(
                      showToastNotification({
                        content: t("Please close other upload operations before proceeding!"),
                        type: TOAST_TYPE_ERROR,
                      }),
                    );
                  } else {
                    if (!permissionCheck) {
                      dispatch(
                        showToastNotification({
                          content: t("Insufficient permission"),
                          type: TOAST_TYPE_ERROR,
                        }),
                      );
                      return;
                    }
                    handleSelectOption({ ...args, item });
                  }
                  handleClose();
                }}
              >
                <span className="flex items-center gap-2">
                  <IconComponent name={item.iconName} width="1rem" height="1rem" />
                  {item.text}
                </span>
              </MenuItem>
            ),
          )}
        </Menu>
        {/* Import submenu */}
        <Menu
          anchorEl={importSubmenuAnchor}
          open={importSubmenuOpen}
          onClose={handleImportSubmenuClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          MenuListProps={{ onMouseLeave: handleImportSubmenuClose }}
        >
          {dropDownButtonItems
            .find((item: DropdownEntry) => item.id === "import")
            ?.subItems?.map((subItem: DropdownEntry) => (
              <MenuItem
                sx={{ fontSize: "0.875rem", paddingBottom: "0.5rem !important", paddingTop: "0.5rem !important" }}
                key={subItem.id}
                onClick={(args) => {
                  if (isFileVersionUploading) {
                    dispatch(
                      showToastNotification({
                        content: t("Please close other upload operations before proceeding!"),
                        type: TOAST_TYPE_ERROR,
                      }),
                    );
                  } else {
                    if (!permissionCheck) {
                      dispatch(
                        showToastNotification({
                          content: t("Insufficient permission"),
                          type: TOAST_TYPE_ERROR,
                        }),
                      );
                      return;
                    }
                    handleSelectOption({ ...args, item: subItem });
                  }
                  handleClose();
                }}
              >
                <span className="flex items-center gap-2">
                  <IconComponent name={subItem.iconName} width="1rem" height="1rem" />
                  {subItem.text}
                </span>
              </MenuItem>
            ))}
        </Menu>
      </Box>
    );
  };

  const getToolBarItems = () => {
    // Both branches assign, so no placeholder initialiser.
    let toolbar: FileManagerToolbarItem[];
    if (fileManagerAsPopup) {
      toolbar = [];
    } else {
      toolbar = [
        {
          name: "Upload",
          id: "upload",
          align: "Left" as ToolbarItem["align"],
          disabled: !toolBarBtnState.upload,
          show: true,
          template: getUploadDropDown,
        },
        {
          name: t("Delete"),
          id: "delete",
          prefixIcon: "delete",
          align: "Left" as ToolbarItem["align"],
          disabled: !toolBarBtnState.delete,
          show: true,
          ["data-testid"]: "deleteFileOrFolder",
        },
        {
          name: t("Rename"),
          id: "rename",
          prefixIcon: "edit",
          align: "Left" as ToolbarItem["align"],
          disabled: !toolBarBtnState.rename,
          show: true,
          ["data-testid"]: "renameFileOrFolder",
        },
        {
          name: t("Download"),
          id: "download",
          prefixIcon: "download",
          align: "Left" as ToolbarItem["align"],
          disabled: !toolBarBtnState.download,
          show: true,
          ["data-testid"]: "downloadFileOrFolder",
        },
        {
          show: (window as Window & { electronAPI?: unknown }).electronAPI,
          // Spelt to match `ToolbarItem["align"]` ("Left" | "Right"), like every entry above. INERT on
          // the live path: `FileManagerComponent` forwards these to the grid's `ToolBar.tsx`, which
          // buckets by `rightAllign`/`sticky` and never reads `align` at all — the only reader of the
          // lowercase spelling is `cubeComponent/FileManager/FileManagerToolbar.tsx`, which nothing
          // imports.
          align: "Right",
          template: () => (
            <FolderSyncTemplate
              spaceId={spaceId}
              projectId={projectId}
              spaceData={spaceData}
              currentFolder={currentFolder}
            />
          ),
        },
      ];
    }
    return toolbar;
  };

  const toolbarItems = getToolBarItems();

  const renderFileManager = () => (
    <FileManagerComponent
      contextMenuSettings={{
        // Handed over as-is. `FileManagerMenuOption` (useFileManager.tsx) now declares the contract
        // `useFileManagerFiles.handleMenuClick` actually invokes — `onClick(selectedFiles)` — as a
        // method, matching `ContextMenuItem.onClick`, so the list IS a `ContextMenuItem[]` and the two
        // row views meet bivariantly. The mapper is keyed by its three literal menu names rather than
        // `Record<string, …>`, so reading one is no longer `| undefined` either.
        file: fileManagerOptionsMapper.fileOptions,
        folder: fileManagerOptionsMapper.folderOptions,
        layout: fileManagerOptionsMapper.layoutOptions,
        visible: !disableContextMenu,
      }}
      disableDragAndDropUpload={disableDragAndDropUpload}
      menuOpen={menuOpen}
      path={path!}
      showFolderTree={showFolderTree}
      beforeSending={beforeSending}
      onFileLoad={onFileLoad}
      onSuccess={onSuccess}
      allowDragAndDropUpload={!layout.FILE_MANAGER?.IMPORT_ONLY_UPLOADS}
      fileColumns={gridColumns}
      externalLinkCounts={externalLinkCounts}
      defaultPinnedColumns={pinnedColumnIds}
      setCurrentFolder={setCurrentFolder}
      // @ts-expect-error -- the local `FileManagerInstance` omits `dataView.getAllSelectedItems`, which the shared `FileManagerHandle` requires. The ref is only used for `refresh`, so the local shape was written narrower than the handle it is assigned to.
      ref={fileManagerRef}
      toolbarClick={toolbarClick}
      toolbarItems={toolbarItems}
      allowViewFileOptions={true}
      fileManagerData={fileManagerDataWithDisabled}
      setFileManagerData={setFileManagerData}
      onFileClick={onFileClick}
      onFileSelect={onFileSelect}
      preSearchText={preSearchText ?? undefined}
      projectMode={projectMode}
      projectId={projectId}
    />
  );

  const getProjectContent = () => {
    // Seat / license gate takes precedence over everything.
    if (moduleAccess.ready && moduleAccess.portalSeat === PORTAL_NO_SEAT) {
      return <ProjectModuleNoAccessIllustration />;
    }
    const spaceRoots = (fileManagerData.files || []).filter((f: FileRow) => f.isSpaceRoot);
    const hasAccessibleSpace = spaceRoots.some((f: FileRow) => f.permission?.read);
    // Once the project read has finished, if the user can access no Space (all locked, or the project has
    // none), show the "not part of any Space" illustration — never the tree / a project-as-cwd fallback.
    if (fileManagerData.projectLoaded && !hasAccessibleSpace) {
      return (
        <Box className="flex flex-col w-full h-full">
          {/* "Folders" label pinned at the top, centered */}
          <div className="flex gap-2 w-full justify-center text-2xl text-text_light">
            <IconComponent name={"folder"} color="#666A84" showGradient width="2rem" height="2rem" />
            {t("Folders")}
          </div>
          <Box className="flex flex-1 flex-col gap-4 items-center justify-center">
            <IconComponent name="NoData" width="18.75rem" height="18.8125rem" />
            <Box className="font-semibold text-lg">
              <Text>{t("You're not part of any Space.")}</Text>
            </Box>
            <Text className="text-sm text-gray-500 text-center">
              {t(
                "You need to be part of at least one Space to work on this module. Please contact your Project Manager.",
              )}
            </Text>
          </Box>
        </Box>
      );
    }
    return <div className="w-full h-full relative flex">{renderFileManager()}</div>;
  };

  const getContent = () => {
    if (projectMode) {
      return getProjectContent();
    }
    // `getCurrSpaceData` (src/actions/breadcrumbsdata.ts) stores `{ noAccess: true }` in this slice
    // when the space read comes back empty. `SpaceDataItem` does not declare the flag, so it is
    // probed on the value rather than asserted onto the slice type.
    if ("noAccess" in spaceData && spaceData.noAccess) {
      return <Forbidden />;
    } else if (spaceData.id) {
      return <>{renderFileManager()}</>;
    } else {
      return "";
    }
  };

  const newContainerCreateModal = () => (
    <Box className="w-full">
      <Box className="my-4">
        <Text>{t("Format")}</Text>
        <Box className="mt-2">
          <FormControl
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                height: "2rem",
              },
            }}
          >
            <Select
              // The chosen format is `resourceType.value`: that is what the MenuItems below are keyed
              // by and what `handleCreateContainer` (useFileManager) reads. The `text` this used to
              // bind to was ALWAYS `undefined` — on a Select change MUI replaces `event.target` with
              // exactly `{ value, name }` (@mui/material/Select/SelectInput.js), so nothing ever wrote
              // it. Binding `undefined` left the Select UNCONTROLLED (`useControlled` treats
              // `value === undefined` as such), so it did still show the picked item off its own
              // internal state, and because the modal is unmounted on close
              // (`{openContainerModal && getContainerModal()}`) that state was discarded each time —
              // the two read the same on screen. This binds the state the rest of the flow acts on.
              value={typeof resourceType.value === "string" ? resourceType.value : ""}
              onChange={(e) => {
                setFileTypeValue("");
                setResourceType({ value: e.target.value });
              }}
            >
              <MenuItem value="RESOURCE_TYPE_FILE">
                <Text>{t("File")}</Text>
              </MenuItem>
              <MenuItem value="RESOURCE_TYPE_EMBED">
                <Text>{t("Embed")}</Text>
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box className="my-4">
        <Text> {t("Name")} </Text>
        {currentFolder.fileNomenclature?.fields?.length ? (
          <Box className="mt-2 flex gap-2">
            {currentFolder.fileNomenclature.fields.map((field: { _id?: string; name?: string }, index: number) => (
              <NomenclatureField
                key={`${field._id}-${index}`}
                args={{ id: 1 }}
                field={field}
                index={index}
                value={nomenclatureValues[index] ?? ""}
                handleNomenclatureChange={handleNomenclatureChange}
              />
            ))}
          </Box>
        ) : (
          <Box className="mt-2">
            <TextFieldComponent onChange={handleContainerName} placeholder={t("Container Name")} />
          </Box>
        )}
      </Box>
      {resourceType.value == "RESOURCE_TYPE_EMBED" && (
        <Box className="my-4">
          <Text>{t("URL")}</Text>
          <Box className="mt-2">
            <TextFieldComponent
              // `handleEmbedUrl` declares a loose `{ name?, url? }` bag but only reads `args.target`,
              // so the change event is handed over as that one field instead of being reshaped.
              onChange={(event) => handleEmbedUrl({ target: event.target })}
              placeholder={t("Embed URL")}
            />
          </Box>
        </Box>
      )}
      {resourceType.value == "RESOURCE_TYPE_FILE" && (
        <>
          <Box className="my-4">
            <Text>{t("File Type")}</Text>
            <Box className="mt-2">
              <AutoComplete
                options={containerFileTypes.map((x: string) => ({ text: x }))}
                valueField="text"
                getOptionLabel={(option) => option.text || ""}
                value={
                  containerFileTypes
                    .map((x: string) => ({ text: x }))
                    .find((opt: { text: string }) => opt.text === fileTypeValue) || null
                }
                onChange={(_event, newValue) => {
                  setFileTypeValue(newValue?.text ?? null);
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props} className="flex items-center gap-1 mt-1">
                    <span className="mb-1 flex">{getFileIcon(option.text)}</span>
                    <span>
                      <Text>{option.text}</Text>
                    </span>
                  </Box>
                )}
                renderInput={(params) => <TextFieldComponent {...params} placeholder={t("Select file Type")} />}
              />
            </Box>
          </Box>
          <Box className="my-4">
            <FormPreview
              formData={{ template: containerFormTemplate }}
              disabled={false}
              allowPadding={false}
              allowBorder={false}
              fullWidthInput
              removeIcons
              handleData={handleCustomFieldsData}
              formValidator={formValidator}
            />
          </Box>
        </>
      )}
    </Box>
  );

  /**
   * `openRenameModal` is `{ state, oldName } | boolean`: the two close handlers below replace the
   * whole object with `false`, so the boolean arm carries no `state` and reads as closed.
   */
  const isRenameModalOpen = typeof openRenameModal === "object" && openRenameModal.state;

  /**
   * The rows the transmittal modal is opened on. `selectedFile` is `Partial<FileManagerRow> |
   * FileManagerRow[]` and the single-row arm belongs to the RENAME flow; `handleFileTransmit`
   * (useFileManager) — the only thing that opens that modal — is also the only writer that stores the
   * array. Handing the object arm over would give `useTransmittalReferences.buildInitialState` a
   * non-array to `.forEach` over, so it is answered with the prop's own "no references" value.
   */
  const selectedTransmittalRows = Array.isArray(selectedFile) ? selectedFile : null;

  const getRenameModal = () => (
    <ModalStateDialogComponent
      footerContent={
        <Box className="flex w-full gap-4">
          <ButtonComponent
            onClick={handleSaveNewName}
            disabled={!newName.trim() || hasInvalidChars(newName)}
            buttonType="gradientButton"
            data-testid="saveNewName"
          >
            <Text>{t("Save")}</Text>
          </ButtonComponent>
          <ButtonComponent
            onClick={() => {
              setOpenRenameModal(false);
              setNewName("");
            }}
            buttonType="borderButton"
            data-testid="cancelNewName"
          >
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </Box>
      }
      onCancel={() => {
        setOpenRenameModal(false);
      }}
      headerTitle={t("Rename")}
      modelContent={
        <div className="w-full flex flex-col gap-1">
          <TextFieldComponent
            onChange={(args) => setNewName(args.target.value)}
            placeholder={t("Enter a new name")}
            value={newName}
            data-testid="enterNewName"
          />
          {hasInvalidChars(newName) && (
            <Text className="text-red-600 whitespace-normal text-xs">
              {t(
                'The file or folder name contains invalid characters. Please use a different name. Valid file or folder name cannot end with a dot or space, and cannot contain any of the following characters: \\ / : * ? " < > |',
              )}
            </Text>
          )}
        </div>
      }
      isVisible={isRenameModalOpen}
      width="25%"
      allowModalPadding
    />
  );

  const getFolderCreationModal = () => (
    <ModalStateDialogComponent
      footerContent={
        <Box className="flex w-full gap-4">
          <ButtonComponent
            onClick={handleCreateFolder}
            disabled={!folderName.trim() || hasInvalidChars(folderName)}
            buttonType="gradientButton"
            data-testid="createFolderBtn"
          >
            <Text>{t("Create")}</Text>
          </ButtonComponent>
          <ButtonComponent
            onClick={() => {
              handleCloseFolder();
            }}
            buttonType="borderButton"
            data-testid="discardFolderBtn"
          >
            <Text>{t("Discard")}</Text>
          </ButtonComponent>
        </Box>
      }
      onCancel={handleCloseFolder}
      headerTitle={t("New Folder")}
      modelContent={
        <div className="w-full flex flex-col gap-1">
          <TextFieldComponent
            onChange={handleFolderName}
            placeholder={t("Enter your folder name")}
            data-testid="enterFolderName"
          />
          {hasInvalidChars(folderName) && (
            <Text className="text-red-600 whitespace-normal text-xs">
              {t(
                'The file or folder name contains invalid characters. Please use a different name. Valid file or folder name cannot end with a dot or space, and cannot contain any of the following characters: \\ / : * ? " < > |',
              )}
            </Text>
          )}
        </div>
      }
      isVisible={openFolderCreationModal}
      width="25%"
      allowModalPadding
    />
  );

  const getContainerModal = () => (
    <ModalStateDialogComponent
      footerContent={
        <Box className="flex w-full gap-4">
          <ButtonComponent
            onClick={handleCreateContainer}
            disabled={containerName.trim() == "" || !fileTypeValue}
            buttonType="gradientButton"
          >
            <Text>{t("Create")}</Text>
          </ButtonComponent>
          <ButtonComponent
            onClick={() => {
              handleModalClose();
            }}
            buttonType="borderButton"
          >
            <Text>{t("Discard")}</Text>
          </ButtonComponent>
        </Box>
      }
      headerTitle={t("New Container")}
      onCancel={handleModalClose}
      modelContent={newContainerCreateModal()}
      isVisible={openContainerModal}
      width="60%"
      allowModalPadding
    />
  );

  const getFileManagerPopup = () => {
    const selectedFiles = getSelectedFilesInFileManager();

    if (openFileImport) {
      return (
        <FileManagerAsPopup
          projectId={projectId}
          handleFileSelectedButtonClick={handleFileImportCallback}
          handleFileSelectedCancelClick={closeFileImportPopup}
          workspaceId={spaceId}
          id="fileManagerAsPopup"
          useAttachedExternalVolume
          disableContextMenu
          heading={t("Import Files")}
          hideAllTabs
          minimumFiles={0}
        />
      );
    } else if (openMergeEstimateModal) {
      return (
        <FileManagerAsPopup
          projectId={projectId}
          handleFileSelectedButtonClick={createMergeEstimateModal}
          handleFileSelectedCancelClick={closeMergeEstimateModal}
          workspaceId={spaceId}
          id="fileManagerAsPopup"
          hideIssues
          hideTasks
          hideClashTests
          hideSchedules
          hideContracts
          heading={t("Merged Estimates")}
          allowInput
          inputLabel="Merged Estimate Name"
          minimumFiles={0}
          selectedFilesData={selectedFiles}
          createMerge
        />
      );
    } else if (openMergeModal) {
      return (
        <FileManagerAsPopup
          projectId={projectId}
          handleFileSelectedButtonClick={saveFileMergeModal}
          handleFileSelectedCancelClick={closeFileMergeModal}
          workspaceId={spaceId}
          id="fileManagerAsPopup"
          hideIssues
          hideTasks
          hideClashTests
          hideSchedules
          hideContracts
          hideReviews
          hideAllTabs
          heading={t("Merge")}
          allowInput
          inputLabel="Name"
          minimumFiles={0}
          selectedFilesData={selectedFiles}
          createMerge
          addEmptyContainerToList
        />
      );
    } else {
      return "";
    }
  };

  return (
    <Box className="w-full h-full flex relative bg-white flex-col" data-testid="filemanagerComponentRoot">
      {getContent()}
      {openDeleteConfirmationModal && (
        <FileDelete
          workspaceId={currentFolder.spaceId ?? spaceId}
          selectedFiles={selectedFilesToDelete}
          onCloseModal={handleDeleteModalClose}
          onDeleteSuccess={handleDeleteSuccess}
          path={path}
          requestDirectoryTotal={showFolderTree}
          directoryTotalProjectId={projectMode ? projectId : undefined}
        />
      )}
      {showContainerImportModal && (
        <ExcelImport
          showImportModal={showContainerImportModal}
          setShowImportModal={setShowContainerImportModal}
          source={excelImportSources.FILES}
          sourceId="a"
          refreshDataGrid={reloadFileManager}
          importMode={importMode}
          currentData={{
            projectId,
            hierarchyId: currentFolder.hierarchyId,
            parentId: currentFolder.hierarchyId,
            spaceId: currentFolder.spaceId ?? spaceId,
            folderName: currentFolder.name,
            filterPath: currentFolder.filterPath,
          }}
        />
      )}
      {isRenameModalOpen && getRenameModal()}
      {openCreateTransmittalModal && (
        <CreateTransmittal
          isOpen={openCreateTransmittalModal}
          onClose={() => setOpenCreateTransmittalModal(false)}
          projectId={projectId}
          // `selectedFile` is the single-row shape OR an array of rows; only the array arm holds a
          // row to read `spaceId` off, and the transmit flow is the one that stores that array.
          selectedSpaceId={
            (Array.isArray(selectedFile) ? selectedFile[0]?.spaceId : undefined) ?? currentFolder.spaceId ?? spaceId
          }
          selectedReferences={
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- BLOCKED ON THE DECLARATION: `TransmittalReference` (features/transmittals/hooks/transmittal.types.ts:102) requires `remarks: string`, `fileType: string`, `dateCreated: number` and a BRANDED `fileVersion: ObjectIdString`; a grid row is `folderItemSchema` shaped (packages/server/folders/folders.schema.ts:47), where `fileVersion` is a plain optional string and `remarks`/`fileType` are not declared at all. Those rows really are what that grid renders — `CreateTransmittal.handleReferencesSelectedButtonClick` feeds it the same file-manager rows and defaults `remarks` with `|| ""` — so supplying the missing members here would fabricate an epoch-0 `dateCreated` and a `0` size rather than fix anything. Same standing gap as `SharingContent` and `FilesInReview`.
            selectedTransmittalRows as (typeof selectedTransmittalRows & TransmittalReference[]) | null
          }
        />
      )}
      {createReviewModal.show && (
        <CreateReviewModal
          spaceId={currentFolder.spaceId ?? spaceId}
          refreshGrid={reloadFileManager}
          closeModal={closeReviewModal}
          projectId={projectId}
          initialFiles={getSelectedFilesInFileManager()}
        />
      )}
      {/* Shareable external links (CP-283). One host renders whichever of the create / manage /
          stats / revoke dialogs the flow is on; the context-menu handlers in useFileManager drive it. */}
      <ExternalLinksHost state={externalLinks} />
      {/* FR-24: a cut/paste of an item that itself carries an external link breaks that link, so the
          user is warned with a real cancel BEFORE the move runs. */}
      {!!pendingMoveWarning && (
        <MoveBreaksLinksModal
          linkCount={pendingMoveWarning.linkCount}
          itemCount={pendingMoveWarning.itemCount}
          onConfirm={confirmPendingMove}
          onCancel={() => setPendingMoveWarning(null)}
        />
      )}
      {openContainerModal && getContainerModal()}
      {openFolderCreationModal && getFolderCreationModal()}
      {(openFileImport || openMergeEstimateModal || openMergeModal) && getFileManagerPopup()}
    </Box>
  );
});

FileManager.displayName = "FileManager";

export default FileManager;
